import { MarketplaceOrderApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { AccountCountCache, AccountNameCache } from '../../../lib/cache/AccountNameCache';
import { onlyUnique, onlyUniqueNonBlank } from '../../../lib/helpers/ArrayHelper';

type AccountSummary = {
  accountId: string;
  accountName: string;
  customerReferences: string[];
  totalAccountsForSource: number;
  accountSource: string;
};

export const GetOrderReferences = (item: SmartSendItemApiModel): string[] =>
  item.MarketplaceOrderLines.map(GetCustomerReferenceOrMarketplaceOrderlineId).filter(onlyUnique);

export const GetCustomerReferenceNumber = (x: MarketplaceOrderApiModel): string => x.CustomerReference;

export const GetCollatedCustomerReferenceNumbers = (orders: MarketplaceOrderApiModel[]): string[] =>
  orders.map(GetCustomerReferenceNumber).filter(onlyUniqueNonBlank);

export const GetItemAccountNamesForMultiAccountSources = (item: SmartSendItemApiModel): string[] =>
  item.MarketplaceOrderLines.filter((x) => AccountCountCache[x.Source?.Id] > 1)
    .map((x) => AccountNameCache[x.Source.AccountId])
    .filter(onlyUniqueNonBlank);

export const GetOrderReferencesStr = (item: SmartSendItemApiModel): string => GetOrderReferences(item).join(' ');

export const GetOrderReferenceBreakdown = (orders: MarketplaceOrderApiModel[]): AccountSummary[] => {
  const output: AccountSummary[] = [];
  const accountIds = orders.map((x) => x.Source?.AccountId).filter(onlyUniqueNonBlank);

  accountIds.forEach((id) => {
    const accountOrders = orders.filter((x) => x.Source.AccountId === id);
    output.push({
      accountId: id,
      accountName: AccountNameCache[id],
      accountSource: accountOrders[0]?.Source?.Id,
      totalAccountsForSource: AccountCountCache[accountOrders[0]?.Source?.Id],
      customerReferences: accountOrders.map(GetCustomerReferenceNumber).filter(onlyUniqueNonBlank)
    });
  });

  output.push({
    accountId: 'manual',
    accountName: 'Manual',
    accountSource: 'manual',
    totalAccountsForSource: 1,
    customerReferences: orders
      .filter((x) => x.Source.Id === 'manual')
      .map(GetCustomerReferenceNumber)
      .filter(onlyUniqueNonBlank)
  });

  output.push({
    accountId: 'csv',
    accountName: 'CSV',
    accountSource: 'csv',
    totalAccountsForSource: 1,
    customerReferences: orders
      .filter((x) => x.Source.Id === 'csv')
      .map(GetCustomerReferenceNumber)
      .filter(onlyUniqueNonBlank)
  });

  return output;
};

export const GetCustomerReferenceOrMarketplaceOrderlineId = (x: MarketplaceOrderApiModel): string => {
  if (x.CustomerReference) {
    return x.CustomerReference;
  }
  return x.MarketplaceOrderLineId ? `${x.MarketplaceOrderId}_${x.MarketplaceOrderLineId}` : x.MarketplaceOrderId;
};

export const GetPickingListMarketPlaceOrderIds = (item: SmartSendItemApiModel, x: MarketplaceOrderApiModel): string => {
  if (item.Recipient?.Meta['buyerId']?.Value) {
    return item.Recipient?.Meta['buyerId']?.Value;
  }
  if (x.CustomerReference && AccountNameCache[x.Source.AccountId]) {
    return `${AccountNameCache[x.Source.AccountId]} ${x.CustomerReference}`;
  }
  if (x.CustomerReference) {
    return x.CustomerReference;
  }
  return x.MarketplaceOrderLineId ? `${x.MarketplaceOrderId}_${x.MarketplaceOrderLineId}` : x.MarketplaceOrderId;
};
